import React from "react";
import { Badge, Group, Stack, Table, Text } from "@mantine/core";

/** Wire-shape row from ``/api/crm/weekly-schedules/`` — one entry per
 *  slot. ``day_of_week`` matches ``WeeklySchedule.day_of_week``
 *  (0 = Monday … 6 = Sunday). ``available: false`` rows are blocked
 *  windows (lunch break, etc.) that override outer available slots
 *  via last-match-wins on the backend. */
export interface WeeklyScheduleEntry {
  day_of_week: number;
  start: string;
  end: string;
  available?: boolean;
}

export interface WeeklyScheduleViewProps {
  schedules: WeeklyScheduleEntry[];
  /** Tri-state ``Tenant.effective_opening_override``. ``null``/
   *  ``undefined`` = follow schedule (the default), ``true`` =
   *  force open, ``false`` = force closed. Shown as a banner above
   *  the per-day table when set. */
  override?: boolean | null;
  /** Override day labels (e.g. localised). Length 7, Mon..Sun. */
  dayNames?: string[];
}

const DEFAULT_DAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/** Read-side display for a tenant's weekly opening hours — the read half
 *  of the pair whose write half is ``WeeklyScheduleEditor``. The two do not
 *  share a value shape: the editor works in ``SlotsByDay``, a map keyed
 *  ``"mon"``…``"sun"``, while this takes the wire shape — flat
 *  ``WeeklyScheduleEntry`` rows with a numeric ``day_of_week`` (0 = Monday).
 *  Call sites that do both convert between them. ``SlotsEditor`` is the
 *  single-day list the editor mounts once per weekday; the available/blocked
 *  semantics and colour conventions are shared across all three.
 *  Tenant detail page + doorbell detail page both render this — no
 *  more bespoke per-page tables. */
export const WeeklyScheduleView: React.FC<WeeklyScheduleViewProps> = ({
  schedules,
  override,
  dayNames = DEFAULT_DAY_NAMES,
}) => {
  const byDay: WeeklyScheduleEntry[][] = Array.from({ length: 7 }, () => []);
  for (const s of schedules) {
    if (s.day_of_week >= 0 && s.day_of_week <= 6) {
      byDay[s.day_of_week].push(s);
    }
  }
  byDay.forEach((day) => day.sort((a, b) => a.start.localeCompare(b.start)));

  const overrideSet = override === true || override === false;

  return (
    <Stack gap="md">
      {overrideSet && (
        <Badge color={override ? "green" : "red"} variant="filled" size="lg">
          Override active: {override ? "Forced open" : "Forced closed"}
        </Badge>
      )}
      <Table verticalSpacing="sm" highlightOnHover>
        <Table.Tbody>
          {dayNames.map((day, i) => {
            const slots = byDay[i] ?? [];
            const hasAvailable = slots.some((s) => s.available !== false);
            return (
              <Table.Tr key={i}>
                <Table.Td w={120}>
                  <Text fw={500} fz="sm">{day}</Text>
                </Table.Td>
                <Table.Td>
                  {slots.length === 0 ? (
                    <Badge variant="light" color="red" size="lg">Closed</Badge>
                  ) : !hasAvailable ? (
                    <Badge variant="light" color="red" size="lg">Closed</Badge>
                  ) : (
                    <Group gap="xs">
                      {slots.map((slot, j) => {
                        const isAvailable = slot.available !== false;
                        return (
                          <Badge
                            key={j}
                            variant="light"
                            color={isAvailable ? "green" : "gray"}
                            size="lg"
                            ff="monospace"
                          >
                            {!isAvailable && "Blocked "}
                            {slot.start} – {slot.end}
                          </Badge>
                        );
                      })}
                    </Group>
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};
